import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  InMemoryCache,
  createHttpLink,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { RetryLink } from "@apollo/client/link/retry";
import { version } from "../../package.json";
import { MyGenerationsQueryVariables } from "../__generated__/graphql";
import { auth } from "../api/firebaseAuth";
import { LOCALE_KEY } from "./LanguageProvider";
import {
  GENERATION_COMPLETE_FRAGMENT,
  GENERATION_FRAGMENT,
} from "../api/graphql/generation";
import { createFragmentRegistry } from "@apollo/client/cache";

function ApolloClientProvider(props: { children: JSX.Element }) {
  const httpLink = createHttpLink({
    uri: import.meta.env.VITE_GRAPHQL_URL,
  });

  const retryLink = new RetryLink({
    delay: {
      initial: 500,
      max: Infinity,
      jitter: true,
    },
    attempts: {
      max: 5,
      retryIf: (error) => Boolean(error),
    },
  });

  const authLink = setContext(async (_, { headers }) => {
    const token = await auth.currentUser?.getIdToken();
    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : "",
        "x-app-platform": "web",
        "x-app-name": "mojo",
        "x-app-version": version,
        "x-user-locale": localStorage.getItem(LOCALE_KEY) ?? navigator.language,
      } as Record<string, string>,
    };
  });

  const client = new ApolloClient({
    link: ApolloLink.from([authLink, retryLink, httpLink]),
    cache: new InMemoryCache({
      fragments: createFragmentRegistry(
        GENERATION_FRAGMENT,
        GENERATION_COMPLETE_FRAGMENT,
      ),
      typePolicies: {
        Query: {
          fields: {
            myAiGenerationsAnonymous: {
              keyArgs: false,
              merge: makeInfiniteScrollMerge<MyGenerationsQueryVariables>(
                (args) => args?.cursor,
              ),
            },
          },
        },
      },
    }),
  });

  return <ApolloProvider client={client}>{props.children}</ApolloProvider>;
}

export default ApolloClientProvider;

function makeInfiniteScrollMerge<
  Args,
  Item extends { _id: string } = { _id: string },
>(argsToCursor: (args: Args | null) => string | null | undefined) {
  return (
    existing: { data?: Item[] },
    incoming: { data: Item[] },
    {
      args,
      readField,
    }: {
      args: Record<string, unknown> | null;
      readField: (fieldName: string, item: Item) => string; // function to read a specific field from the item
    },
  ) =>
    mergeInfinite(existing, incoming, {
      cursor: argsToCursor(args as Args | null),
      readField,
    });
}

/**
 * Updates data in the cache when a new page is loaded in infinite scroll
 * @param existing - current cache
 * @param incoming - fresh data of a specific page
 * @param options - additional graphql options
 * @returns
 */
function mergeInfinite<T extends { _id: string }>(
  existing: { data?: T[] },
  incoming: { data: T[] },
  {
    cursor,
    readField,
  }: {
    cursor?: string | null;
    readField: (fieldName: string, item: T) => string; // function to read a specific field from the item
  },
) {
  // Special case when the first page is loaded
  if (!cursor) {
    // Case 1: first page ids are the same as in cache
    //  - If all the items of the first page are the same, we can keep the cache as it is: we return `existing`.
    // Case 2: first page ids changed
    // - This means some external update happened: for example, an update from the DB or from an other tab that is not reflected in the current cache.
    //   In that case, the cache must be completely overwritten because all cursors are possibly invalid or shifted, which could corrupt local data: we return `incoming`.
    //   As a result, only the first page is available in cache, and all the next pages will need to be refetched.
    for (let i = 0; i < incoming.data.length; ++i) {
      if (
        !existing?.data?.[i] || // no cache for the incoming data, we can overwrite it
        readField("_id", incoming.data[i]) !==
          readField("_id", existing.data[i])
      ) {
        return incoming;
      }
    }
    return existing;
  }

  // Merge incoming data with the existing data in the cache
  const merged = existing?.data ? existing.data.slice(0) : [];
  const mongoId = getMongoIdFromCursor(cursor);
  const offset = offsetFromMongoId(merged, mongoId, readField);
  for (let i = 0; i < incoming.data.length; ++i) {
    merged[offset + i] = incoming.data[i];
  }
  return { ...incoming, data: merged };
}

function getMongoIdFromCursor(cursor: string) {
  const decoded = atob(cursor);
  const { _id } = JSON.parse(decoded) as { _id: string };
  return _id;
}

function offsetFromMongoId<T extends { _id: string }>(
  items: T[],
  mongoId: string | undefined | null,
  readField: (fieldName: string, item: T) => string,
) {
  for (let i = items.length - 1; i >= 0; --i) {
    const item = items[i];
    if (readField("_id", item) === mongoId) {
      return i + 1;
    }
  }
  return items.length;
}
